import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Grid, Grid2, IconButton, MenuItem, Modal, Paper, Select, Snackbar, Table, TableBody, TableCell, TableSortLabel, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import axios from "axios";
import { makeGetRequestToUrl, makePostRequestToUrl } from '../commonFunc.jsx';
import config from '../../config/config.js';
import { Label } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import TokenError from "../tokenError";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { ReactComponent as UploadIcon } from '../../assets/icons/uploadIcon.svg';
import prevIcon from '../../assets/icons/left-arrow.svg';
import nextIcon from '../../assets/icons/right-arrow.svg';

// TODO: confirm these endpoints with the backend
const FETCH_MERCHANTS_ENDPOINT = '/merchant/get_details';
const SAVE_MERCHANT_ENDPOINT = '/merchant/add_update_merchant';
const DOCUMENT_STATUS_ENDPOINT = '/merchant/approve_reject_merchant_document';
const REQUIRED_DOCS_ENDPOINT = '/merchant/get_required_documents';
const UPLOAD_IMAGE_ENDPOINT = '/upload_image';

const IMAGE_SEPARATOR = ';;;;';

// Saved docs may come back as "url1;;;;url2" or as an array — handle both
const parseImages = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') return value.split(IMAGE_SEPARATOR).map((s) => s.trim()).filter(Boolean);
    return [];
};

// Overall status (merchant_document_status), as returned by the API
const MERCHANT_STATUS = {
    0: { label: 'Pending', color: '#F5A623', bg: '#FFF6E5' },
    1: { label: 'Approved', color: '#0DBA78', bg: '#EFFBF7' },
    2: { label: 'Rejected', color: '#D14545', bg: '#FBEEEE' },
};
const getMerchantStatus = (status) => MERCHANT_STATUS[Number(status)] || MERCHANT_STATUS[0];

// Per-document status, same codes as the driver document view
const DOC_APPROVED = 1;
const DOC_PENDING = 2;
const DOC_REJECTED = 3;
const DOC_STATUS = {
    1: { label: 'Approved', color: '#0DBA78' },
    2: { label: 'Pending review', color: '#F5A623' },
    3: { label: 'Rejected', color: '#C71F1F' },
    4: { label: 'Expired', color: '#C71F1F' },
    5: { label: 'Expiring soon', color: '#F5BB19' },
};
// Treat a missing status as pending so new uploads can be reviewed
const getDocStatus = (doc) => {
    const s = Number(doc?.status);
    return DOC_STATUS[s] ? s : DOC_PENDING;
};

const labelStyle = { fontSize: '16px', fontWeight: '500', color: '#83888E' };
const inputStyle = { height: '56px', fontSize: '16px', padding: '8px 14px', backgroundColor: '#FFFFFF' };
const paperStyle = { padding: '30px', maxWidth: '800px', margin: '20px auto', backgroundColor: '#FFFFFF', borderRadius: '12px' };
const sectionTitleStyle = { marginBottom: '20px', marginTop: '0px', fontSize: '20px', color: '#1A1E25' };
const errorTextSx = { color: '#d32f2f', fontSize: '12px', mt: '4px', ml: '14px' };

const UploadButton = styled(Button)({
    textTransform: 'none',
    borderRadius: '8px',
    border: '1px dashed #0065FF',
    color: '#0065FF',
    backgroundColor: '#F3F8FF',
    padding: '8px 16px',
    '&:hover': { backgroundColor: '#E6F0FF' },
});

const blankDoc = (doc) => ({
    document_id: doc.document_id,
    document_number: '',
    expiry_date: '',
    images: [],
});

const DetailItem = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontSize: '13px', color: '#83888E' }}>{label}</Typography>
        <Typography sx={{ fontSize: '15px', color: '#1A1E25', fontWeight: 500, wordBreak: 'break-word', mt: '2px' }}>
            {value || '--'}
        </Typography>
    </Box>
);

const Merchants = () => {
    const serverUrl = config.serverUrl;
    const operationsUrl = config.operationsUrl;
    const navigate = useNavigate();

    // ---------- list state ----------
    const [rows, setRows] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('merchant_id');
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;

    // ---------- form state ----------
    const [open, setOpen] = useState(false);
    const [editable, setEditable] = useState(false);
    const [merchantId, setMerchantId] = useState(0);
    const [merchantName, setMerchantName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [originalEmail, setOriginalEmail] = useState('');
    const [alternativeEmail, setAlternativeEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rePassword, setRePassword] = useState('');

    // ---------- documents state (add/edit form) ----------
    const [activeDocs, setActiveDocs] = useState([]);              // config from get_required_documents
    const [merchantDocuments, setMerchantDocuments] = useState([]); // values entered, same order as activeDocs
    const [documentErrors, setDocumentErrors] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [uploadingKey, setUploadingKey] = useState('');
    const docsRequestRef = useRef(0);
    const [docNames, setDocNames] = useState({});                  // document_id -> document_name, for the details modal

    // ---------- details modal state ----------
    const [viewMerchant, setViewMerchant] = useState(null);
    const [docUpdating, setDocUpdating] = useState('');            // `${document_id}-${status}` while a request runs
    const [rejectDoc, setRejectDoc] = useState(null);              // document being rejected (opens reason dialog)
    const [rejectReason, setRejectReason] = useState('');
    const [rejectReasonError, setRejectReasonError] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [rotation, setRotation] = useState(0);

    // ---------- field errors ----------
    const [nameError, setNameError] = useState(false);
    const [phoneError, setPhoneError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [alternativeEmailError, setAlternativeEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [rePasswordError, setRePasswordError] = useState(false);
    const [passwordMismatchError, setPasswordMismatchError] = useState(false);

    // ---------- misc ----------
    const [message, setMessage] = useState('');
    const [snackbar, showSnackbar] = useState(false);
    const [tokenExpired, setTokenExpired] = useState(false);

    const notify = (msg) => {
        setMessage(typeof msg === 'string' ? msg : 'Something went wrong');
        showSnackbar(true);
    };

    const handleTokenExpired = () => {
        console.log('Token is expired. Please login again');
        localStorage.removeItem('token');
        setTokenExpired(true);
        setTimeout(() => navigate('/signup'), 2000);
    };

    const validateEmail = (value) => {
        const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return re.test(String(value).toLowerCase());
    };

    const clearErrors = () => {
        setNameError(false);
        setPhoneError(false);
        setEmailError(false);
        setAlternativeEmailError(false);
        setPasswordError(false);
        setRePasswordError(false);
        setPasswordMismatchError(false);
        setDocumentErrors([]);
    };

    const clearForm = () => {
        setMerchantId(0);
        setMerchantName('');
        setPhoneNumber('');
        setEmail('');
        setOriginalEmail('');
        setAlternativeEmail('');
        setPassword('');
        setRePassword('');
        setMerchantDocuments(activeDocs.map(blankDoc));
        clearErrors();
    };

    // ---------- API: merchants list ----------
    const fetchMerchantData = async () => {
        try {
            const token = localStorage.getItem('token');
            const url = serverUrl + FETCH_MERCHANTS_ENDPOINT;
            const response = await makeGetRequestToUrl(url, { token, timestamp: new Date().getTime() });

            if (response.flag == 102) { handleTokenExpired(); return; }
            if (response.flag == 200) {
                const list = Array.isArray(response.data) ? response.data : [];
                setRows(list);
                // keep an open details modal in sync with the latest data
                setViewMerchant((prev) => prev ? (list.find((m) => m.merchant_id === prev.merchant_id) || prev) : prev);
            }
        } catch (error) {
            console.log('error', error);
        }
    };

    // Document names for the details modal (merchant_documents may only carry document_id)
    const fetchDocNames = async () => {
        try {
            const token = localStorage.getItem('token');
            const url = serverUrl + REQUIRED_DOCS_ENDPOINT;
            const response = await makeGetRequestToUrl(url, { token, timestamp: new Date().getTime() });
            if (response.flag == 102) { handleTokenExpired(); return; }
            const list = Array.isArray(response.data) ? response.data : [];
            setDocNames(Object.fromEntries(list.map((d) => [String(d.document_id), d.document_name])));
        } catch (error) {
            console.log('error', error);
        }
    };

    useEffect(() => {
        fetchMerchantData();
        fetchDocNames();
    }, []);

    // ---------- API: required documents (add/edit form) ----------
    // existingDocs = documents already saved for the merchant (edit mode), matched by document_id
    const fetchRequiredDocs = async (existingDocs = []) => {
        const requestId = ++docsRequestRef.current;
        setDocsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const url = serverUrl + REQUIRED_DOCS_ENDPOINT;
            const response = await makeGetRequestToUrl(url, { token, timestamp: new Date().getTime() });

            if (requestId !== docsRequestRef.current) return; // panel was reopened meanwhile
            if (response.flag == 102) { handleTokenExpired(); return; }

            const docs = (Array.isArray(response.data) ? response.data : []).filter((d) => d.is_active === 1);

            setActiveDocs(docs);
            setMerchantDocuments(docs.map((doc) => {
                const saved = existingDocs.find((e) => String(e.document_id) === String(doc.document_id));
                if (!saved) return blankDoc(doc);
                return {
                    document_id: doc.document_id,
                    document_number: saved.document_number || '',
                    expiry_date: saved.expiry_date ? dayjs(saved.expiry_date).format('YYYY-MM-DD') : '',
                    images: parseImages(saved.images),
                };
            }));
            setDocumentErrors([]);
        } catch (error) {
            console.error('Error fetching required documents:', error);
            if (requestId === docsRequestRef.current) {
                setActiveDocs([]);
                setMerchantDocuments([]);
            }
        } finally {
            if (requestId === docsRequestRef.current) setDocsLoading(false);
        }
    };

    // ---------- document helpers ----------
    const updateDoc = (index, field, value) => {
        setMerchantDocuments((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
    };

    const clearDocError = (index, field) => {
        setDocumentErrors((prev) => {
            if (!prev[index]?.[field]) return prev;
            const next = [...prev];
            next[index] = { ...next[index] };
            delete next[index][field];
            return next;
        });
    };

    const handleFileChange = (index, imgIndex) => async (event) => {
        const input = event.target;
        const file = input.files?.[0];
        if (!file) return;

        setUploadingKey(`${index}-${imgIndex}`);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const response = await axios.post(operationsUrl + UPLOAD_IMAGE_ENDPOINT, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const s3Url = response.data?.url;
            if (!s3Url) throw new Error('No URL returned');

            setMerchantDocuments((prev) => prev.map((d, i) => {
                if (i !== index) return d;
                const images = [...(d.images || [])];
                images[imgIndex] = s3Url;
                return { ...d, images };
            }));
            clearDocError(index, 'image');
        } catch (error) {
            console.error('Error uploading image:', error);
            notify('Image upload failed. Please try again.');
        } finally {
            setUploadingKey('');
            input.value = ''; // allow re-selecting the same file
        }
    };

    // Required docs must be complete. Optional docs are only checked if the user started filling them.
    const validateDocuments = () => {
        const errors = activeDocs.map((doc, i) => {
            const d = merchantDocuments[i] || {};
            const images = (d.images || []).filter(Boolean);
            const started = Boolean(d.document_number?.trim() || d.expiry_date || images.length);
            const err = {};

            if (doc.is_required !== 1 && !started) return err;

            if (doc.is_doc_number_required === 1 && !d.document_number?.trim()) {
                err.documentNumber = 'Document number is required';
            }
            if (doc.is_doc_expired === 1 && !d.expiry_date) {
                err.expiryDate = 'Expiry date is required';
            }
            const needed = Number(doc.no_of_images) || 0;
            if (needed > 0 && images.length < needed) {
                err.image = needed > 1 ? `Please upload all ${needed} images` : 'Please upload the image';
            }
            return err;
        });

        setDocumentErrors(errors);
        return errors.every((e) => Object.keys(e).length === 0);
    };

    // ---------- open / close: add/edit panel ----------
    const handleOpenAddMerchant = () => {
        clearForm();
        setEditable(false);
        setOpen(true);
        fetchRequiredDocs();
    };

    const handleOpenEditMerchant = (row) => {
        clearErrors();
        setMerchantId(row.merchant_id);
        setMerchantName(row.merchant_name || '');
        setPhoneNumber(row.merchant_phone_number || '');
        setEmail(row.merchant_email || '');
        setOriginalEmail(row.merchant_email || '');
        setAlternativeEmail(row.alternative_email ?? '');
        setPassword('');
        setRePassword('');
        setEditable(true);
        setOpen(true);
        fetchRequiredDocs(Array.isArray(row.merchant_documents) ? row.merchant_documents : []);
    };

    const handleClose = () => {
        setOpen(false);
        setEditable(false);
        setOriginalEmail('');
    };

    const handleReset = () => {
        if (editable) {
            // keep identity of the record being edited, clear everything else
            setMerchantName('');
            setPhoneNumber('');
            setAlternativeEmail('');
            setMerchantDocuments(activeDocs.map(blankDoc));
            clearErrors();
        } else {
            clearForm();
        }
    };

    // ---------- open / close: details modal ----------
    const handleOpenDetails = (row) => {
        setRejectDoc(null);
        setViewMerchant(row);
    };

    const handleCloseDetails = () => {
        if (docUpdating) return; // don't close mid-request
        setViewMerchant(null);
        setRejectDoc(null);
    };

    const openPreview = (src) => {
        setRotation(0);
        setPreviewImage(src);
    };

    const openRejectDialog = (doc) => {
        setRejectReason('');
        setRejectReasonError(false);
        setRejectDoc(doc);
    };

    const closeRejectDialog = () => {
        if (docUpdating) return;
        setRejectDoc(null);
    };

    // ---------- API: save ----------
    const handleSubmit = async () => {
        const token = localStorage.getItem('token');

        if (merchantName.trim() === '') {
            setNameError(true); notify('Please enter a valid merchant name'); return;
        }
        if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
            setPhoneError(true); notify('Please enter a valid phone number'); return;
        }
        if (!email || !validateEmail(email)) {
            setEmailError(true); notify('Please enter a valid email address'); return;
        }
        if (alternativeEmail && !validateEmail(alternativeEmail)) {
            setAlternativeEmailError(true); notify('Please enter a valid alternative email address'); return;
        }
        if (alternativeEmail && alternativeEmail.toLowerCase() === email.toLowerCase()) {
            setAlternativeEmailError(true); notify('Alternative email must differ from the primary email'); return;
        }
        if (!editable) {
            if (!password) { setPasswordError(true); notify('Please enter a valid password'); return; }
            if (!rePassword) { setRePasswordError(true); notify('Please re-enter your password'); return; }
            if (password !== rePassword) { setPasswordMismatchError(true); notify('Passwords do not match'); return; }
        }
        if (docsLoading || uploadingKey) {
            notify('Please wait for documents to finish loading'); return;
        }
        if (!validateDocuments()) {
            notify('Please complete all required document information'); return;
        }

        const documents = merchantDocuments
            .filter((d) => d.document_number?.trim() || d.expiry_date || (d.images || []).some(Boolean))
            .map((d) => ({
                document_id: d.document_id,
                document_number: d.document_number.trim(),
                expiry_date: d.expiry_date,
                images: (d.images || []).filter(Boolean).join(IMAGE_SEPARATOR),
            }));

        const payload = {
            token,
            merchant_name: merchantName.trim(),
            merchant_phone_number: phoneNumber,
            merchant_docs: documents,
        };

        if (editable) {
            if (email.trim() !== (originalEmail || '').trim()) payload.merchant_email = email.trim();
            payload.merchant_id = merchantId;
        } else {
            payload.merchant_email = email.trim();
            payload.merchant_password = password;
        }

        if (alternativeEmail) payload.alternative_email = alternativeEmail.trim();

        try {
            const url = operationsUrl + SAVE_MERCHANT_ENDPOINT;
            const response = await makePostRequestToUrl(url, payload);

            if (response.flag == 102) { handleTokenExpired(); return; }

            if (response.flag === 200) {
                notify(response.message || 'Merchant saved successfully');
                handleClose();
                fetchMerchantData();
            } else {
                notify(response.message || 'Failed to save merchant');
            }
        } catch (error) {
            console.error('Error while saving merchant:', error);
            notify('Something went wrong while saving the merchant');
        }
    };

    // ---------- API: approve / reject a single document ----------
    const handleDocumentStatus = async (doc, status, reason = '') => {
        if (docUpdating || !viewMerchant) return;
        const token = localStorage.getItem('token');
        const merchantIdForDoc = viewMerchant.merchant_id;

        const payload = {
            token,
            merchant_id: merchantIdForDoc,
            document_id: doc.document_id,
            status,
        };
        if (status === DOC_REJECTED) payload.reason = reason.trim();

        setDocUpdating(`${doc.document_id}-${status}`);
        try {
            const url = operationsUrl + DOCUMENT_STATUS_ENDPOINT;
            const response = await makePostRequestToUrl(url, payload);

            if (response.flag == 102) { handleTokenExpired(); return; }

            if (response.flag === 200) {
                notify(response.message || (status === DOC_APPROVED ? 'Document approved' : 'Document rejected'));
                // update the open modal right away; the refetch below brings the server's version
                setViewMerchant((prev) => {
                    if (!prev || prev.merchant_id !== merchantIdForDoc) return prev;
                    return {
                        ...prev,
                        merchant_documents: (prev.merchant_documents || []).map((d) =>
                            String(d.document_id) === String(doc.document_id)
                                ? { ...d, status, reason: status === DOC_REJECTED ? reason.trim() : d.reason }
                                : d
                        ),
                    };
                });
                setRejectDoc(null);
                fetchMerchantData();
            } else {
                notify(response.message || 'Failed to update the document');
            }
        } catch (error) {
            console.error('Error while updating document status:', error);
            notify('Something went wrong while updating the document');
        } finally {
            setDocUpdating('');
        }
    };

    const submitReject = () => {
        if (!rejectReason.trim()) {
            setRejectReasonError(true);
            return;
        }
        handleDocumentStatus(rejectDoc, DOC_REJECTED, rejectReason);
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        showSnackbar(false);
    };

    // ---------- sort / filter / paginate ----------
    const handleRequestSort = (property) => {
        const isAscending = orderBy === property && order === 'asc';
        setOrder(isAscending ? 'desc' : 'asc');
        setOrderBy(property);
        setRows((prev) => [...prev].sort((a, b) => {
            if (a[property] < b[property]) return isAscending ? 1 : -1;
            if (a[property] > b[property]) return isAscending ? -1 : 1;
            return 0;
        }));
    };

    const filteredRows = rows.filter((row) => statusFilter === '' ? true : String(row.merchant_status) === statusFilter);
    const totalEntries = filteredRows.length;
    const totalPages = Math.ceil(totalEntries / rowsPerPage);
    const pagedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const hasRequiredDoc = activeDocs.some((doc) => doc.is_required === 1);
    const hasDocErrors = documentErrors.some((err) => err && Object.keys(err).length > 0);

    // details modal derived values
    const viewStatusMeta = getMerchantStatus(viewMerchant?.merchant_status);
    const viewDocs = Array.isArray(viewMerchant?.merchant_documents) ? viewMerchant.merchant_documents : [];
    const approvedCount = viewDocs.filter((d) => getDocStatus(d) === DOC_APPROVED).length;
    const pendingCount = viewDocs.filter((d) => getDocStatus(d) === DOC_PENDING).length;

    const setEnabled = async(row, val) => {
        try {
            const token = localStorage.getItem('token');
            const body = {
                token: token,
                is_active: val,
                merchant_id: row.merchant_id,
            };
            if(row.role_id==1){
                setMessage('This is the default role. You cannot disable it.');
                showSnackbar(true);
                return;
            }

            const response = await makePostRequestToUrl(operationsUrl + SAVE_MERCHANT_ENDPOINT, body);

            if (response.flag==200) {
                fetchMerchantData();
                setMessage(response.message);
                showSnackbar(true);
            } 
            else {
                setMessage(response.message || 'Login failed. Please try again.');
                showSnackbar(true);
            }
        } catch (error) {
            setMessage('An error occurred. Please try again later.');
            showSnackbar(true);
        }
    }

    return (
        <>
        <div>
            {tokenExpired && (<TokenError />)}

            <Typography style={{ fontSize: '28px', fontWeight: '500' }}>
                Merchants
            </Typography>

            {/* Filters */}
            <Grid2 container spacing={2} alignItems="center" sx={{ marginTop: '2%', marginBottom: '2%' }}>
                <Grid2>
                    <Select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} displayEmpty
                        style={{ width: '220px', height: '50px', borderRadius: '8px', backgroundColor: statusFilter ? '#F9F9F9' : '#FFFFFF' }}>
                        <MenuItem value="">Status</MenuItem>
                        <MenuItem value="0">Pending</MenuItem>
                        <MenuItem value="1">Approved</MenuItem>
                        <MenuItem value="2">Rejected</MenuItem>
                    </Select>
                </Grid2>

                <Grid2>
                    <Button
                        variant="contained" onClick={handleOpenAddMerchant}
                        style={{ width: '185px', height: '50px', backgroundColor: '#000000', textTransform: 'none', borderRadius: '8px' }}>
                        + Add Merchant
                    </Button>
                </Grid2>
            </Grid2>

            {/* Add / Edit side panel */}
            {open && (
                <div style={{
                    position: 'fixed', top: 65, right: 0, height: 'calc(100vh - 65px)', width: '50%',
                    backgroundColor: '#f9f9f9', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)', zIndex: 1000,
                    display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: 20,
                }}>
                    <Box>
                        <div>
                            <IconButton aria-label="close" onClick={handleClose}
                                style={{ position: 'absolute', top: '15px', left: '10px', color: '#333', zIndex: 10 }}>
                                <CloseIcon style={{ fontSize: '32px', fontWeight: 'bold' }} />
                            </IconButton>
                            <Typography variant="h6" style={{ marginBottom: '0px', marginTop: '42px', fontSize: '24px', color: '#4E545D' }}>
                                {editable ? 'Edit Merchant' : 'Add Merchant'}
                            </Typography>
                        </div>

                        <div style={{ position: 'relative', padding: '20px 0 40px 0' }}>

                            {/* Company Details */}
                            <Paper elevation={3} style={{ ...paperStyle, margin: '0 auto' }}>
                                <Typography variant="h6" style={sectionTitleStyle}>Company Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Label style={labelStyle}>Merchant Name<span style={{ color: 'red' }}>*</span></Label>
                                        <TextField sx={{ marginTop: '2%' }}
                                            placeholder="Merchant Name" fullWidth variant="outlined" type="text"
                                            value={merchantName} error={nameError}
                                            helperText={nameError ? 'Please enter a valid merchant name' : ''}
                                            onChange={(e) => { setMerchantName(e.target.value); setNameError(false); }}
                                            InputProps={{ style: inputStyle }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Label style={labelStyle}>Phone Number<span style={{ color: 'red' }}>*</span></Label>
                                        <PhoneInput
                                            defaultCountry="IN"
                                            value={phoneNumber}
                                            onChange={(value) => { setPhoneNumber(value || ''); setPhoneError(false); }}
                                            placeholder="Enter phone number"
                                            style={{ height: '56px', fontSize: '16px', borderRadius: '12px', alignItems: 'normal', marginTop: '2%' }}
                                        />
                                        {phoneError && <Typography sx={errorTextSx}>Please enter a valid phone number</Typography>}
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Label style={labelStyle}>Email<span style={{ color: 'red' }}>*</span></Label>
                                        <TextField sx={{ marginTop: '2%' }}
                                            placeholder="Email" fullWidth variant="outlined" type="email"
                                            value={email} error={emailError} disabled={editable}
                                            helperText={emailError ? 'Please enter a valid email address' : ''}
                                            onChange={(e) => { setEmail(e.target.value); setEmailError(false); }}
                                            InputProps={{ style: inputStyle }}
                                        />
                                    </Grid>

                                    {/* <Grid item xs={12} sm={6}>
                                        <Label style={labelStyle}>Alternative Email</Label>
                                        <TextField sx={{ marginTop: '2%' }}
                                            placeholder="Alternative Email (optional)" fullWidth variant="outlined" type="email"
                                            value={alternativeEmail} error={alternativeEmailError}
                                            helperText={alternativeEmailError ? 'Please enter a valid alternative email' : ''}
                                            onChange={(e) => { setAlternativeEmail(e.target.value); setAlternativeEmailError(false); }}
                                            InputProps={{ style: inputStyle }}
                                        />
                                    </Grid> */}
                                </Grid>
                            </Paper>

                            {/* Passwords (add only) */}
                            {!editable && (
                                <Paper elevation={3} style={paperStyle}>
                                    <Typography variant="h6" style={sectionTitleStyle}>Passwords</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Label style={labelStyle}>Enter Password<span style={{ color: 'red' }}>*</span></Label>
                                            <TextField sx={{ marginTop: '2%' }}
                                                placeholder="Enter Password" fullWidth variant="outlined" type="password"
                                                autoComplete="new-password"
                                                value={password} error={passwordError}
                                                helperText={passwordError ? 'Please enter a valid password' : ''}
                                                onChange={(e) => { setPassword(e.target.value); setPasswordError(false); setPasswordMismatchError(false); }}
                                                InputProps={{ style: inputStyle }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Label style={labelStyle}>Re-Enter Password<span style={{ color: 'red' }}>*</span></Label>
                                            <TextField sx={{ marginTop: '2%' }}
                                                placeholder="Re-Enter Password" fullWidth variant="outlined" type="password"
                                                autoComplete="new-password"
                                                value={rePassword} error={rePasswordError || passwordMismatchError}
                                                helperText={rePasswordError ? 'Please re-enter the password' : passwordMismatchError ? 'Passwords do not match' : ''}
                                                onChange={(e) => { setRePassword(e.target.value); setRePasswordError(false); setPasswordMismatchError(false); }}
                                                InputProps={{ style: inputStyle }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            )}

                            {/* Documents */}
                            <Paper elevation={3} style={paperStyle}>
                                <Typography variant="h6" style={{ ...sectionTitleStyle, marginBottom: '8px' }}>
                                    Documents{hasRequiredDoc && <span style={{ color: 'red' }}>*</span>}
                                </Typography>

                                {hasDocErrors && (
                                    <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                                        Please complete all required document information
                                    </Typography>
                                )}

                                {docsLoading ? (
                                    <Typography sx={{ color: '#9FA3A7' }}>Loading documents…</Typography>
                                ) : activeDocs.length === 0 ? (
                                    <Typography sx={{ color: '#9FA3A7' }}>No documents are configured for merchants.</Typography>
                                ) : (
                                    activeDocs.map((doc, index) => {
                                        const values = merchantDocuments[index] || {};
                                        const err = documentErrors[index] || {};
                                        const hasError = Boolean(err.documentNumber || err.image || err.expiryDate);
                                        const isRequired = doc.is_required === 1;
                                        const imageCount = Number(doc.no_of_images) || 0;

                                        return (
                                            <div key={doc.document_id} style={{
                                                padding: '12px',
                                                border: `1px solid ${hasError ? '#f44336' : '#ccc'}`,
                                                borderRadius: '8px',
                                                backgroundColor: '#F9F9F9',
                                                marginTop: '16px',
                                            }}>
                                                <Typography sx={{ fontWeight: 500 }}>
                                                    {doc.document_name}
                                                    {isRequired && <span style={{ color: 'red' }}>*</span>}
                                                </Typography>

                                                <Box sx={{ backgroundColor: '#fff', padding: '16px', borderRadius: '12px', marginTop: '12px' }}>

                                                    {/* Document number */}
                                                    {doc.is_doc_number_required === 1 && (
                                                        <Box>
                                                            <Label style={labelStyle}>
                                                                Document No.{isRequired && <span style={{ color: 'red' }}>*</span>}
                                                            </Label>
                                                            <TextField sx={{ marginTop: '8px' }}
                                                                placeholder="Document Number" fullWidth variant="outlined" type="text"
                                                                value={values.document_number || ''}
                                                                onChange={(e) => { updateDoc(index, 'document_number', e.target.value); clearDocError(index, 'documentNumber'); }}
                                                                error={!!err.documentNumber}
                                                                helperText={err.documentNumber}
                                                                InputProps={{
                                                                    style: { height: '48px', fontSize: '16px', padding: '8px 14px', backgroundColor: '#FFFFFF', borderRadius: '8px' },
                                                                }}
                                                            />
                                                        </Box>
                                                    )}

                                                    {/* Expiry date */}
                                                    {doc.is_doc_expired === 1 && (
                                                        <Box sx={{ marginTop: doc.is_doc_number_required === 1 ? '16px' : 0 }}>
                                                            <Label style={labelStyle}>
                                                                Document Expiry Date{isRequired && <span style={{ color: 'red' }}>*</span>}
                                                            </Label>
                                                            <Box sx={{ width: '100%', marginTop: '8px' }}>
                                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                    <DatePicker
                                                                        disablePast
                                                                        format="DD/MM/YYYY"
                                                                        value={values.expiry_date ? dayjs(values.expiry_date) : null}
                                                                        onChange={(newValue) => {
                                                                            const valid = newValue && newValue.isValid();
                                                                            updateDoc(index, 'expiry_date', valid ? newValue.format('YYYY-MM-DD') : '');
                                                                            if (valid) clearDocError(index, 'expiryDate');
                                                                        }}
                                                                        sx={{
                                                                            '& .MuiOutlinedInput-root': { height: '48px', borderRadius: '8px', backgroundColor: '#FFFFFF' },
                                                                        }}
                                                                        slotProps={{
                                                                            textField: {
                                                                                fullWidth: true,
                                                                                placeholder: 'Select expiry date',
                                                                                error: !!err.expiryDate,
                                                                                helperText: err.expiryDate,
                                                                            },
                                                                        }}
                                                                    />
                                                                </LocalizationProvider>
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* Images (hidden when the document needs 0 images) */}
                                                    {imageCount > 0 && (
                                                        <Box sx={{ marginTop: (doc.is_doc_number_required === 1 || doc.is_doc_expired === 1) ? '16px' : 0 }}>
                                                            <Label style={labelStyle}>
                                                                Upload Image{imageCount > 1 ? 's' : ''}
                                                                {isRequired && <span style={{ color: 'red' }}>*</span>}
                                                            </Label>
                                                            {err.image && (
                                                                <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                                                    {err.image}
                                                                </Typography>
                                                            )}

                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: '8px' }}>
                                                                {Array.from({ length: imageCount }).map((_, imgIndex) => {
                                                                    const inputId = `merchant-upload-${doc.document_id}-${imgIndex}`;
                                                                    const imgSrc = values.images?.[imgIndex];
                                                                    const isUploading = uploadingKey === `${index}-${imgIndex}`;

                                                                    return (
                                                                        <Box key={imgIndex} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                            <input
                                                                                type="file"
                                                                                accept="image/*"
                                                                                style={{ display: 'none' }}
                                                                                capture={doc.is_gallery_restricted === 1 ? 'environment' : undefined}
                                                                                onChange={handleFileChange(index, imgIndex)}
                                                                                id={inputId}
                                                                                disabled={isUploading}
                                                                            />
                                                                            <label htmlFor={inputId}>
                                                                                <UploadButton
                                                                                    component="span"
                                                                                    disabled={isUploading}
                                                                                    startIcon={<UploadIcon style={{ width: 20, height: 20 }} />}
                                                                                >
                                                                                    {isUploading ? 'Uploading…' : imgSrc ? `Replace ${imgIndex + 1}` : `Upload ${imgIndex + 1}`}
                                                                                </UploadButton>
                                                                            </label>

                                                                            {imgSrc && (
                                                                                <img
                                                                                    src={imgSrc}
                                                                                    alt={`${doc.document_name} ${imgIndex + 1}`}
                                                                                    onClick={() => openPreview(imgSrc)}
                                                                                    style={{
                                                                                        marginTop: '8px', height: '80px', width: '80px', objectFit: 'cover',
                                                                                        borderRadius: '6px', border: '1px solid #ccc', cursor: 'pointer',
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </div>
                                        );
                                    })
                                )}
                            </Paper>

                            <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'center', gap: '20px' }}>
                                <Button variant="contained" onClick={handleSubmit}
                                    disabled={Boolean(uploadingKey)}
                                    sx={{ width: '140px', height: '50px', textTransform: 'none', fontSize: '16px', backgroundColor: '#0065FF', color: 'white' }}>
                                    {editable ? 'Update' : 'Save'}
                                </Button>
                                <Button variant="contained" onClick={handleReset}
                                    sx={{ width: '140px', height: '50px', textTransform: 'none', fontSize: '16px', backgroundColor: '#F0F0F0', color: '#B0B0B0' }}>
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </Box>
                </div>
            )}

            {/* Table */}
            <TableContainer className="table-container" sx={{ maxHeight: '70vh' }}>
                <Table stickyHeader className="scrollable-table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" className="table-header">
                                <TableSortLabel active={orderBy === 'merchant_id'} direction={orderBy === 'merchant_id' ? order : 'asc'}
                                    onClick={() => handleRequestSort('merchant_id')}>
                                    Id
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center" className="table-header">
                                <TableSortLabel active={orderBy === 'merchant_name'} direction={orderBy === 'merchant_name' ? order : 'asc'}
                                    onClick={() => handleRequestSort('merchant_name')}>
                                    Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="center" className="table-header">Phone Number</TableCell>
                            <TableCell align="center" className="table-header">Email</TableCell>
                            {/* <TableCell align="center" className="table-header">Alternative Email</TableCell> */}
                            <TableCell align="center" className="table-header">Document Status</TableCell>
                            <TableCell align="center" className="table-header">Account Status</TableCell>
                            <TableCell align="center" className="table-header">Action</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {pagedRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography variant="body1">No data found</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {pagedRows.map((row) => {
                            const statusMeta = getMerchantStatus(row.merchant_status);
                            return (
                                <TableRow key={row.merchant_id}>
                                    <TableCell align="center"><Typography sx={{ minWidth: '80px' }}>{row.merchant_id ?? '--'}</Typography></TableCell>
                                    <TableCell align="center"><Typography sx={{ minWidth: '150px' }}>{row.merchant_name || '--'}</Typography></TableCell>
                                    <TableCell align="center"><Typography sx={{ minWidth: '120px' }}>{row.merchant_phone_number || '--'}</Typography></TableCell>
                                    <TableCell align="center"><Typography sx={{ minWidth: '150px' }}>{row.merchant_email || '--'}</Typography></TableCell>
                                    {/* <TableCell align="center"><Typography sx={{ minWidth: '150px' }}>{row.alternative_email || '--'}</Typography></TableCell> */}
                                    <TableCell align="center">
                                        <Typography sx={{ minWidth: '100px', fontWeight: '500', color: statusMeta.color }}>
                                            {statusMeta.label}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {row.merchant_status==1 ?
                                            <div style={{ display: 'flex', justifyContent:'center'}}>
                                                <Button variant="contained" disabled={row.merchant_status!=1} onClick={() => setEnabled(row,1)}
                                                    style={{
                                                        backgroundColor: row.is_active ? '#0DBA78' : '#F4F4F4', minWidth: '80px',padding: '6px 16px',
                                                        color:row.is_active?'#FFFFFF':'#9E9E9E',textTransform:'none',borderRadius:'8px 0 0 8px', boxShadow: 'none',
                                                    }}
                                                >
                                                    Activate
                                                </Button>
                                                <Button variant="contained" disabled={row.merchant_status!=1} onClick={() => setEnabled(row, 0)}
                                                    style={{
                                                        backgroundColor: !row.is_active ? '#D14545' : '#F4F4F4', color: !row.is_active ? '#FFFFFF' : '#9E9E9E',
                                                        textTransform:'none', borderRadius:'0 8px 8px 0', boxShadow:'none',padding:'6px 16px', minWidth:'80px',
                                                    }}
                                                >
                                                    Deactivate
                                                </Button>
                                            </div>
                                            :
                                            <Typography sx={{color:row.is_active==1 ? 'green' : '#D14545'}}> {row.is_active==1 ? 'Active' : 'Inactive'} </Typography>
                                        }
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
                                            <Button
                                                variant="contained" disableRipple
                                                onClick={() => handleOpenDetails(row)}
                                                startIcon={<VisibilityIcon sx={{ fontSize: '18px !important' }} />}
                                                sx={{
                                                    backgroundColor: '#F3F8FF', color: '#0065FF', border: '1px solid #0065FF',
                                                    textTransform: 'none', borderRadius: '8px', width: 186, height: 38,
                                                    boxShadow: 'none', transition: 'all 0.15s ease',
                                                    '&:hover': { backgroundColor: '#0065FF', color: '#fff', transform: 'translateY(-1px)' },
                                                    '&:active': { backgroundColor: '#0065FF', color: '#fff', transform: 'translateY(0)' },
                                                }}
                                            >
                                                View Details
                                            </Button>

                                            <Button
                                                variant="contained" disableRipple
                                                onClick={() => handleOpenEditMerchant(row)}
                                                startIcon={
                                                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M4.84304 9.45074L5.39054 6.89324C5.47025 6.53784 5.64979 6.21258 5.90804 5.95574L10.9105 0.998238C11.4178 0.481469 12.1066 0.182825 12.8305 0.165738C13.3765 0.157914 13.9031 0.368518 14.293 0.750738C15.1452 1.75759 15.0352 3.26115 14.0455 4.13324L9.04304 9.13574C8.7862 9.39399 8.46095 9.57353 8.10554 9.65324L5.54804 10.2007H5.40554C5.21858 10.2079 5.04 10.1228 4.92774 9.97316C4.81547 9.82347 4.78382 9.62822 4.84304 9.45074ZM6.70304 6.75824C6.59796 6.86007 6.52496 6.99043 6.49304 7.13324L6.12554 8.88074L7.87304 8.50574C8.01585 8.47382 8.14621 8.40082 8.24804 8.29574L13.2505 3.29324C13.7986 2.85451 13.9073 2.0636 13.498 1.49324C13.3188 1.32202 13.0783 1.23012 12.8305 1.23824C12.4035 1.25539 11.9999 1.43834 11.7055 1.74824L6.70304 6.75824Z" fill="currentColor"/>
                                                        <path d="M13.933 6.19574C13.6241 6.19978 13.3746 6.44927 13.3705 6.75824V11.0257C13.3806 11.6289 13.148 12.2109 12.7249 12.641C12.3019 13.0711 11.7238 13.3133 11.1205 13.3132H3.97305C2.72607 13.2888 1.72688 12.2729 1.72305 11.0257V3.90824C1.74352 2.65955 2.7617 1.65807 4.01055 1.65824H8.27805C8.58871 1.65824 8.84055 1.4064 8.84055 1.09574C8.84055 0.785078 8.58871 0.533238 8.27805 0.533238H3.97305C2.10292 0.533125 0.581098 2.03823 0.560547 3.90824V11.0257C0.560547 12.9104 2.08838 14.4382 3.97305 14.4382H11.0905C12.9723 14.4341 14.4956 12.9075 14.4955 11.0257V6.75824C14.4915 6.44927 14.242 6.19978 13.933 6.19574Z" fill="currentColor"/>
                                                    </svg>
                                                }
                                                sx={{
                                                    backgroundColor: '#EFFBF7', color: '#0DBA78', border: '1px solid #0DBA78',
                                                    textTransform: 'none', borderRadius: '8px', width: 186, height: 38,
                                                    boxShadow: 'none', transition: 'all 0.15s ease',
                                                    '&:hover': { backgroundColor: '#0DBA78', color: '#fff', transform: 'translateY(-1px)' },
                                                    '&:active': { backgroundColor: '#0DBA78', color: '#fff', transform: 'translateY(0)' },
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                <div className="custom-pagination">
                    <div className="pagination-info">
                        {totalEntries === 0
                            ? 'Showing 0 entries'
                            : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, totalEntries)} of ${totalEntries} entries`}
                    </div>
                    <div className="pagination-controls">
                        <button onClick={() => setPage(page - 1)} disabled={page === 0} className="pagination-arrow">
                            <img src={prevIcon} alt="Previous" />
                        </button>
                        <div className="pagination-numbers">
                            {[...Array(totalPages)].map((_, index) => (
                                <button key={index} onClick={() => setPage(index)}
                                    className={`pagination-number ${page === index ? 'active' : ''}`}>
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                        <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} className="pagination-arrow">
                            <img src={nextIcon} alt="Next" />
                        </button>
                    </div>
                </div>
            </TableContainer>

            {/* ---------- Merchant details modal ---------- */}
            <Modal open={Boolean(viewMerchant)} onClose={handleCloseDetails}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', md: '80%' }, maxWidth: 960, maxHeight: '90vh',
                    bgcolor: '#FFFFFF', borderRadius: '12px', boxShadow: 24, outline: 'none',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}>
                    {viewMerchant && (
                        <>
                            {/* Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #E5E7EB' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1A1E25', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {viewMerchant.merchant_name || 'Merchant'}
                                    </Typography>
                                    <Box sx={{
                                        px: 1.25, py: '3px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                                        color: viewStatusMeta.color, backgroundColor: viewStatusMeta.bg, flexShrink: 0,
                                    }}>
                                        {viewStatusMeta.label}
                                    </Box>
                                </Box>
                                <IconButton onClick={handleCloseDetails} sx={{ color: '#6B7280' }} disabled={Boolean(docUpdating)}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>

                            {/* Body */}
                            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
                                {/* Merchant info */}
                                <Box sx={{ backgroundColor: '#F9F9F9', borderRadius: '8px', px: 2, py: 1.25 }}>
                                    <Typography sx={{ fontWeight: 500 }}>Merchant Details</Typography>
                                </Box>
                                <Box sx={{
                                    display: 'grid', gap: 2.5, px: 2, py: 2.5,
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                                }}>
                                    <DetailItem label="Merchant ID" value={viewMerchant.merchant_id} />
                                    <DetailItem label="Phone Number" value={viewMerchant.merchant_phone_number} />
                                    <DetailItem label="Email" value={viewMerchant.merchant_email} />
                                    {/* <DetailItem label="Alternative Email" value={viewMerchant.alternative_email} /> */}
                                </Box>

                                {/* Documents */}
                                <Box sx={{ backgroundColor: '#F9F9F9', borderRadius: '8px', px: 2, py: 1.25, mt: 1, display: 'flex', alignItems: 'center' }}>
                                    <Typography sx={{ fontWeight: 500 }}>Documents</Typography>
                                    {viewDocs.length > 0 && (
                                        <Typography sx={{ fontSize: '13px', color: '#83888E', ml: 'auto' }}>
                                            {approvedCount} of {viewDocs.length} approved
                                            {pendingCount > 0 && ` · ${pendingCount} awaiting review`}
                                        </Typography>
                                    )}
                                </Box>

                                {viewDocs.length === 0 ? (
                                    <Typography sx={{ color: '#9FA3A7', px: 2, py: 3 }}>
                                        This merchant hasn't uploaded any documents.
                                    </Typography>
                                ) : (
                                    viewDocs.map((doc, docIndex) => {
                                        const images = parseImages(doc.images);
                                        const name = doc.document_name || docNames[String(doc.document_id)] || `Document ${doc.document_id}`;
                                        const statusCode = getDocStatus(doc);
                                        const docStatus = DOC_STATUS[statusCode];
                                        const approving = docUpdating === `${doc.document_id}-${DOC_APPROVED}`;
                                        const anyBusy = Boolean(docUpdating);

                                        return (
                                            <Box key={`merchant-doc-${doc.document_id}-${docIndex}`} sx={{
                                                border: `1px solid ${statusCode === DOC_PENDING ? '#F5D9A8' : '#E5E7EB'}`,
                                                borderRadius: '10px', mt: 2, overflow: 'hidden',
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, backgroundColor: '#FCFCFD', borderBottom: '1px solid #F0F0F0' }}>
                                                    <Typography sx={{ fontWeight: 500, color: '#1A1E25' }}>
                                                        {name} <span style={{color:'grey', fontWeight:400, fontSize:'14px'}}>({doc.is_required==1 ? 'Mandatory' : 'Optional'})</span>
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: docStatus.color }}>
                                                        {docStatus.label} 
                                                    </Typography>
                                                    {doc.document_number && (
                                                        <Typography sx={{ color: '#0065FF', border: '1px solid #D1D5DB', borderRadius: '6px', padding: '3px 8px', marginLeft: 'auto', fontSize: '14px' }}>
                                                            {doc.document_number}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <Box sx={{ px: 2, py: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                                    {/* Images */}
                                                    <Box sx={{ flex: '1 1 320px' }}>
                                                        {images.length === 0 ? (
                                                            <Typography sx={{ color: '#9FA3A7', fontSize: '14px' }}>No images uploaded.</Typography>
                                                        ) : (
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                                {images.map((src, imageIndex) => (
                                                                    <Box key={`${doc.document_id}-${imageIndex}`} sx={{ textAlign: 'center' }}>
                                                                        <Box
                                                                            onClick={() => openPreview(src)}
                                                                            sx={{
                                                                                height: 102, width: 102, borderRadius: '12px', overflow: 'hidden',
                                                                                border: '2px solid #0065FF', display: 'flex', justifyContent: 'center', alignItems: 'center',
                                                                                cursor: 'pointer', backgroundColor: '#F9FAFB',
                                                                            }}
                                                                        >
                                                                            <img src={src} alt={`${name} ${imageIndex + 1}`}
                                                                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                                                        </Box>
                                                                        <IconButton size="small" onClick={() => openPreview(src)} sx={{ color: '#0065FF', mt: 0.5 }} aria-label="View image">
                                                                            <VisibilityIcon />
                                                                        </IconButton>
                                                                        <Typography sx={{ fontSize: '12px', color: '#83888E' }}>
                                                                            {images.length === 2 ? (imageIndex === 0 ? 'Front' : 'Back') : `Image ${imageIndex + 1}`}
                                                                        </Typography>
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    {/* Per-document review */}
                                                    <Box sx={{ flex: '0 0 auto', minWidth: 220 }}>
                                                        {statusCode === DOC_APPROVED && (
                                                            <Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <CheckCircleIcon sx={{ color: '#0DBA78', fontSize: 28 }} />
                                                                    <Typography sx={{ color: '#0DBA78', fontSize: '14px', fontWeight: 500 }}>{name} approved</Typography>
                                                                </Box>
                                                                {/* <Button size="small" onClick={() => openRejectDialog(doc)} disabled={anyBusy}
                                                                    sx={{ textTransform: 'none', color: '#83888E', mt: 1, px: 0, '&:hover': { color: '#C71F1F', backgroundColor: 'transparent' } }}>
                                                                    Reject instead
                                                                </Button> */}
                                                            </Box>
                                                        )}

                                                        {statusCode === DOC_REJECTED && (
                                                            <Box>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <CancelIcon sx={{ color: '#C71F1F', fontSize: 28 }} />
                                                                    <Typography sx={{ color: '#C71F1F', fontSize: '14px', fontWeight: 500 }}>{name} rejected</Typography>
                                                                </Box>
                                                                {doc.reason && (
                                                                    <Typography sx={{ color: '#83888E', fontSize: '13px', mt: 0.75 }}>
                                                                        Reason: {doc.reason}
                                                                    </Typography>
                                                                )}
                                                                <Button size="small" onClick={() => handleDocumentStatus(doc, DOC_APPROVED)} disabled={anyBusy}
                                                                    sx={{ textTransform: 'none', color: '#83888E', mt: 1, px: 0, '&:hover': { color: '#0DBA78', backgroundColor: 'transparent' } }}>
                                                                    {/* {approving ? 'Approving…' : 'Approve instead'} */}
                                                                    {approving && 'Approving…'}
                                                                </Button>
                                                            </Box>
                                                        )}

                                                        {(statusCode === 4 || statusCode === 5) && (
                                                            <Typography sx={{ color: docStatus.color, fontSize: '14px', fontWeight: 500 }}>
                                                                {name} {docStatus.label.toLowerCase()}
                                                            </Typography>
                                                        )}

                                                        {statusCode === DOC_PENDING && (
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={() => handleDocumentStatus(doc, DOC_APPROVED)}
                                                                    disabled={anyBusy}
                                                                    sx={{ backgroundColor: '#0DBA78', width: 110, height: 38, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', '&:hover': { backgroundColor: '#0BA96C', boxShadow: 'none' } }}
                                                                >
                                                                    {approving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Approve'}
                                                                </Button>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={() => openRejectDialog(doc)}
                                                                    disabled={anyBusy}
                                                                    sx={{ backgroundColor: '#C71F1F', width: 110, height: 38, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', '&:hover': { backgroundColor: '#A81A1A', boxShadow: 'none' } }}
                                                                >
                                                                    Reject
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })
                                )}
                            </Box>

                            {/* Footer */}
                            <Box sx={{ borderTop: '1px solid #E5E7EB', px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography sx={{ fontSize: '13px', color: '#83888E', mr: 'auto' }}>
                                    {pendingCount > 0
                                        ? 'Approve or reject each document after reviewing its images.'
                                        : 'All documents have been reviewed.'}
                                </Typography>
                                <Button onClick={handleCloseDetails} disabled={Boolean(docUpdating)} variant="outlined"
                                    sx={{ textTransform: 'none', borderRadius: '8px', color: '#4E545D', borderColor: '#D1D5DB', minWidth: 100, height: 38 }}>
                                    Close
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>

            {/* ---------- Reject reason dialog ---------- */}
            <Modal open={Boolean(rejectDoc)} onClose={closeRejectDialog}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: { xs: '92%', sm: 440 }, bgcolor: '#FFFFFF', borderRadius: '12px', boxShadow: 24, outline: 'none', p: 3,
                }}>
                    {rejectDoc && (
                        <>
                            <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#1A1E25' }}>
                                Reject {rejectDoc.document_name || docNames[String(rejectDoc.document_id)] || 'document'}
                            </Typography>
                            <Typography sx={{ fontSize: '13px', color: '#83888E', mt: 0.5 }}>
                                The merchant will see this reason and can upload the document again.
                            </Typography>
                            <TextField
                                autoFocus
                                fullWidth
                                multiline
                                minRows={3}
                                placeholder="e.g. Image is blurry, details are not readable"
                                value={rejectReason}
                                onChange={(e) => { setRejectReason(e.target.value); setRejectReasonError(false); }}
                                error={rejectReasonError}
                                helperText={rejectReasonError ? 'Please enter a reason' : ''}
                                sx={{ mt: 2 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2.5 }}>
                                <Button onClick={closeRejectDialog} disabled={Boolean(docUpdating)} variant="outlined"
                                    sx={{ textTransform: 'none', borderRadius: '8px', color: '#6B7280', borderColor: '#D1D5DB', minWidth: 100, height: 40 }}>
                                    Cancel
                                </Button>
                                <Button onClick={submitReject} disabled={Boolean(docUpdating)} variant="contained"
                                    sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#C71F1F', boxShadow: 'none', minWidth: 120, height: 40, '&:hover': { backgroundColor: '#A81A1A', boxShadow: 'none' } }}>
                                    {docUpdating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Reject'}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>

            {/* ---------- Image preview (with rotate) ---------- */}
            <Modal open={Boolean(previewImage)} onClose={() => setPreviewImage('')}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{
                    position: 'relative', width: '90vw', height: '90vh', maxWidth: '640px', maxHeight: '640px',
                    borderRadius: '12px', overflow: 'hidden', backgroundColor: 'white', outline: 'none',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                }}>
                    {previewImage && (
                        <img
                            src={previewImage}
                            alt="Document preview"
                            onClick={() => setRotation((prev) => (prev + 90) % 360)}
                            style={{
                                maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
                                transform: `rotate(${rotation}deg)`, transition: 'transform 0.3s ease', cursor: 'pointer',
                            }}
                        />
                    )}
                    <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 1 }}>
                        <IconButton
                            onClick={() => setRotation((prev) => (prev + 90) % 360)}
                            sx={{ backgroundColor: '#fff', boxShadow: 2, '&:hover': { backgroundColor: '#f5f5f5' } }}
                            aria-label="Rotate image"
                        >
                            <RotateRightIcon />
                        </IconButton>
                        <IconButton
                            onClick={() => setPreviewImage('')}
                            sx={{ backgroundColor: '#fff', boxShadow: 2, '&:hover': { backgroundColor: '#f5f5f5' } }}
                            aria-label="Close preview"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Modal>

            <Snackbar open={snackbar} autoHideDuration={2000} onClose={handleSnackbarClose} message={message} />
        </div>
        </>
    );
};

export default Merchants;